import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import {
  ArrowLeft, ArrowRight, CheckCircle, Buildings, User, Target, ChatText, Sparkle,
} from "@phosphor-icons/react";

const OBJECTIVES = [
  "Financial Analysis", "Financial Statements", "Investment Readiness",
  "Business Valuation", "Fundraising", "Accounting", "Tax",
  "Financial Modelling", "Dashboard & KPIs", "Other",
];

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];
const REVENUES = ["< €100K", "€100K–€500K", "€500K–€2M", "€2M–€10M", "€10M–€50M", "> €50M"];
const YEARS = ["< 1 year", "1-3 years", "3-5 years", "5-10 years", "10+ years"];
const COUNTRIES = [
  "Côte d'Ivoire", "Nigeria", "Ghana", "Senegal", "Kenya", "South Africa",
  "Egypt", "Morocco", "Tunisia", "Cameroon", "Rwanda", "Ethiopia",
  "Uganda", "Tanzania", "Democratic Republic of Congo", "Angola",
  "France", "United Kingdom", "United States", "India", "United Arab Emirates", "Other",
];

const STEPS = [
  { n: 1, key: "contact", label: "Contact", icon: User },
  { n: 2, key: "company", label: "Company", icon: Buildings },
  { n: 3, key: "objectives", label: "Objectives", icon: Target },
  { n: 4, key: "needs", label: "Business needs", icon: ChatText },
];

export default function RequestAccess() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [form, setForm] = useState({
    contact: { full_name: "", email: "", phone: "" },
    company: {
      company_name: "", country: "Côte d'Ivoire", industry: "",
      company_size: COMPANY_SIZES[1], annual_revenue: REVENUES[1], years_in_business: YEARS[1],
    },
    objectives: [],
    needs: "",
  });

  const setField = (section, key) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: v } }));
  };

  const toggleObjective = (o) => {
    setForm((f) => ({
      ...f,
      objectives: f.objectives.includes(o)
        ? f.objectives.filter((x) => x !== o)
        : [...f.objectives, o],
    }));
  };

  const canProceed = () => {
    if (step === 1) return form.contact.full_name && form.contact.email && form.contact.phone;
    if (step === 2) return form.company.company_name && form.company.country && form.company.industry;
    if (step === 3) return form.objectives.length > 0;
    if (step === 4) return form.needs.trim().length >= 20;
    return false;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/request-access", form);
      setSubmitted(data);
    } catch (err) {
      toast.error(extractApiError(err, "Could not submit your request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background text-foreground grid place-items-center px-4 py-16" data-testid="ra-success">
        <div className="max-w-xl w-full bg-card border border-border rounded-2xl p-8 sm:p-10 card-elev-lg text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-teal-soft text-teal grid place-items-center mb-5">
            <CheckCircle size={30} weight="fill" />
          </div>
          <div className="text-overline text-primary mb-2">Request received</div>
          <h1 className="font-display text-3xl tracking-tight">Thank you, {form.contact.full_name.split(" ")[0]}.</h1>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            {submitted.message}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-xs">
            <span className="text-muted-foreground">Reference:</span>
            <code className="bg-secondary px-3 py-1.5 rounded-md font-mono" data-testid="ra-lead-id">{submitted.lead_id}</code>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to={`/onboarding?email=${encodeURIComponent(form.contact.email)}`}
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors"
              data-testid="ra-track"
            >
              Track my onboarding <ArrowRight size={14} weight="bold" />
            </Link>
            <a
              href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-border bg-card rounded-lg px-5 py-3 text-sm font-semibold hover:bg-secondary transition-colors"
              data-testid="ra-book"
            >
              Book a product demo
            </a>
          </div>
        </div>
      </div>
    );
  }

  const StepIcon = STEPS.find((s) => s.n === step)?.icon || Sparkle;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="request-access-page">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo variant="wordmark" size={32} to="/" testId="ra-logo" />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="ra-back-home">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-24">
        <div className="text-overline text-primary mb-3">Request Access</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-2xl">
          Concierge onboarding for institutional users.
        </h1>
        <p className="mt-4 text-sm text-muted-foreground max-w-xl">
          Complete this 2-minute request and our team will contact you within 24 hours to activate your Orixa Invest software subscription.
        </p>

        {/* Stepper */}
        <div className="mt-10 flex items-center justify-between gap-2 sm:gap-3" data-testid="ra-stepper">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1 flex items-center gap-2 sm:gap-3">
              <div className={`h-9 w-9 rounded-full grid place-items-center text-xs font-semibold flex-shrink-0 transition-colors ${
                step > s.n ? "bg-teal-soft text-teal" : step === s.n ? "bg-primary text-primary-foreground card-elev" : "bg-secondary text-muted-foreground"
              }`} data-testid={`ra-step-dot-${s.n}`}>
                {step > s.n ? <CheckCircle size={16} weight="fill" /> : s.n}
              </div>
              <div className="hidden sm:block text-xs uppercase tracking-wider font-semibold whitespace-nowrap text-muted-foreground">
                {s.label}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${step > s.n ? "bg-teal" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        {/* Form card */}
        <div className="mt-10 bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg bg-teal-soft text-teal grid place-items-center">
              <StepIcon size={20} weight="duotone" />
            </div>
            <div>
              <div className="text-overline text-muted-foreground">Step {step} / 4</div>
              <div className="font-display text-xl tracking-tight">{STEPS.find((s) => s.n === step)?.label}</div>
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-5" data-testid="ra-step-contact">
              <Field label="Full name" value={form.contact.full_name} onChange={setField("contact", "full_name")} placeholder="Marie Dupont" testId="ra-full-name" />
              <Field label="Work email" type="email" value={form.contact.email} onChange={setField("contact", "email")} placeholder="you@company.com" testId="ra-email" />
              <Field label="Phone number" value={form.contact.phone} onChange={setField("contact", "phone")} placeholder="+225 07 12 34 56 78" testId="ra-phone" />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="ra-step-company">
              <Field label="Company name" value={form.company.company_name} onChange={setField("company", "company_name")} placeholder="Dupont Trading SARL" testId="ra-company-name" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Select label="Country" value={form.company.country} onChange={setField("company", "country")} options={COUNTRIES} testId="ra-country" />
                <Field label="Industry" value={form.company.industry} onChange={setField("company", "industry")} placeholder="Logistics" testId="ra-industry" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Select label="Company size" value={form.company.company_size} onChange={setField("company", "company_size")} options={COMPANY_SIZES} testId="ra-size" />
                <Select label="Annual revenue" value={form.company.annual_revenue} onChange={setField("company", "annual_revenue")} options={REVENUES} testId="ra-revenue" />
                <Select label="Years in business" value={form.company.years_in_business} onChange={setField("company", "years_in_business")} options={YEARS} testId="ra-years" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="ra-step-objectives">
              <div className="text-sm text-muted-foreground mb-4">Select all that apply. You can refine these during the discovery call.</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {OBJECTIVES.map((o) => {
                  const active = form.objectives.includes(o);
                  return (
                    <button
                      key={o}
                      type="button"
                      onClick={() => toggleObjective(o)}
                      className={`text-sm px-4 py-3 rounded-lg border transition-colors text-left ${
                        active ? "bg-teal-soft border-teal text-teal font-semibold" : "bg-card border-border hover:border-primary/40 hover:bg-secondary"
                      }`}
                      data-testid={`ra-obj-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="ra-step-needs">
              <label className="text-overline text-muted-foreground block mb-2">What business or financial challenges are you trying to solve?</label>
              <textarea
                value={form.needs}
                onChange={(e) => setForm((f) => ({ ...f, needs: e.target.value }))}
                rows={7}
                placeholder="Describe your current priorities, upcoming milestones, and where Orixa can add the most value..."
                className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm resize-none"
                data-testid="ra-needs"
              />
              <div className={`mt-2 text-xs ${form.needs.trim().length >= 20 ? "text-teal" : "text-muted-foreground"}`}>
                {form.needs.trim().length} / 20 characters minimum
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="ra-prev"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {step < 4 ? (
            <button
              type="button"
              onClick={() => canProceed() && setStep((s) => s + 1)}
              disabled={!canProceed()}
              className="bg-primary text-primary-foreground rounded-lg px-6 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 card-elev"
              data-testid="ra-next"
            >
              Continue <ArrowRight size={14} weight="bold" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!canProceed() || submitting}
              className="bg-primary text-primary-foreground rounded-lg px-6 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 card-elev"
              data-testid="ra-submit"
            >
              {submitting ? "Submitting…" : "Submit request"} <ArrowRight size={14} weight="bold" />
            </button>
          )}
        </div>

        <p className="mt-8 text-xs text-muted-foreground text-center leading-relaxed">
          By submitting this form, you agree to our{" "}
          <Link to="/legal/terms" className="text-primary underline underline-offset-2">Terms</Link> and{" "}
          <Link to="/legal/privacy" className="text-primary underline underline-offset-2">Privacy Policy</Link>.
          Your information stays strictly confidential and is used only to prepare your workspace activation.
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="text-overline text-muted-foreground block mb-2">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-overline text-muted-foreground block mb-2">{label}</label>
      <select
        value={value ?? ""}
        onChange={onChange}
        className="w-full bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
        data-testid={testId}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
